/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {ActionTree} from 'vuex';
import {RootState} from '@/store/types';
import {SearchFilterInterface, SearchStateInterface} from '@/store/modules/search/types';
import {api} from '@/services/http';
import {AxiosError, AxiosResponse} from 'axios';
import {IssueInterface} from '@/store/modules/issues/types';

export const actions: ActionTree<SearchStateInterface, RootState> = {

    /**
     * Search Term.
     *
     * @param {any} commit
     * @param {SearchFilterInterface} filters
     */
    async search({commit}: {commit: any}, filters: SearchFilterInterface): Promise<void>  {

        return api.axios.get('api/issues', {
            params: {
                'title': filters.searchTerm,
                'category.game' : filters.gameId,
                'category' : filters.category,
                'status' : filters.status,
                'order[createdAt]' : filters.orderByDate,
                'order[vote]' : filters.orderByVote,
                'itemsPerPage' : filters.itemsPerPage,
            },
        })
            .then((response: AxiosResponse) => {
                const data: any = response.data['hydra:member'];
                const total: number = response.data['hydra:totalItems'];
                const view: number = response.data['hydra:view'];
                let searchResults: any = data;

                if (filters.searchTerm) {
                    searchResults = data.filter((item: any) => {
                        return item.title.toLowerCase().includes(filters.searchTerm.toLowerCase());
                    });
                }

                const payload: IssueInterface[] = [];

                // todo get the game, platform and counter
                for (const result of searchResults) {
                    payload.push({
                        id: result.id,
                        category:  {
                            id: result.category['@id'],
                            type: result.category['@type'],
                            gameName: result.category.game_name,
                        },
                        status: result.status,
                        title: result.title,
                        game: result.category.game_name ,
                        platforms: result.platforms,
                        voteWeight: data.vote_weight,
                        vote: null === data.vote || undefined === data.vote ? null : {
                            id: data.vote['@id'],
                            type: data.vote['@type'],
                        },
                        canVote: result.can_vote,
                        tags: result.platforms,
                        isOwner: result.is_owner,
                    });
                }

                commit('searchResults', {
                    term: filters.searchTerm,
                    gameId: filters.gameId,
                    results: payload,
                    totalResults: total,
                    pagination: view,
                });

            }, (err: AxiosError) => {
                commit('searchError');
            });
    },

    /**
     * Update gameId.
     *
     * @param {any} commit
     * @param {number} newGameId
     */
    updateGameId({commit}: {commit: any}, newGameId: number): void {
        commit('updateGameId', newGameId);
    },

    /**
     * Update search results.
     *
     * @param {any} commit
     * @param {IssueInterface[]} newSearchResults
     */
    updateSearchResults({commit}: {commit: any}, newSearchResults: IssueInterface[]): void {
        commit('updateSearchResults', newSearchResults);
    },

    /**
     * Clear search.
     *
     * @param {any} commit
     * @param {IssueInterface[]} newSearchResults
     */
    clearSearch({commit}: {commit: any}, newSearchResults: IssueInterface[]): void {
        commit('searchResults', newSearchResults);
    },


    /**
     * Search Customer Support Answers.
     *
     * @param {any} commit
     * @param {string} searchTerm
     */
    async searchCustomerSupport({commit}: {commit: any}, searchTerm: string): Promise<void>  {

        // todo searchCustomerSupport API

        return api.axios.get('api/issues', {
            params: {
                title: searchTerm,
            },
        })
            .then((response: AxiosResponse) => {

                const data: any = response.data['hydra:member'];
                const searchResults: any = data.filter((item: any) => {
                    return item.title.toLowerCase().includes(searchTerm.toLowerCase());
                });

                const payload: any[] = [];
                for (const result of searchResults) {
                    payload.push({
                        id: result.id,
                        category: result.category,
                        status: result.status,
                        title: result.title,
                        game: result.category !== null ? result.category.game_name : '',
                        platform: 'platform',
                        counter: 0,
                        votes: result.customer_votes_count,
                        vote: result.vote,
                });
                }

                commit('searchCustomerSupport', {term: searchTerm, results: payload});

            }, (err: AxiosError) => {
                commit('searchCustomerSupportError');
            });
    },





    /**
     * Search Pagination.
     *
     * @param {any} commit
     * @param {string} SearchUrl
     */
    async searchByUrl({commit}: {commit: any}, SearchUrl: string): Promise<void>  {
        return api.axios.get(SearchUrl, {})
            .then((response: AxiosResponse) => {
                const data: any = response.data['hydra:member'];
                const total: number = response.data['hydra:totalItems'];
                const view: number = response.data['hydra:view'];

                commit('searchResults', {
                    term: '',
                    gameId: '',
                    results: data,
                    totalResults: total,
                    pagination: view,
                });

            }, (err: AxiosError) => {
                commit('searchError');
            });

    },

};
